import { Streamdown } from "streamdown";
import { MemberMention } from "./MemberMention";
import {
  linkRosterMentions,
  normalizeBrokenMemberMentions,
  stripIntelligenceLeaks,
  stripModelCopiedStorageLinks,
  type IntelligenceMemberRoster,
} from "./intelligenceMarkdownUtils";

export type { IntelligenceMemberRoster } from "./intelligenceMarkdownUtils";

interface Props {
  text: string;
  businessId?: string;
  members?: IntelligenceMemberRoster | null;
  isStreaming?: boolean;
}

// Converts @[Name](member:id) → <mention mid="id">Name</mention>
// so streamdown's allowedTags mechanism routes them to our custom component.
function mentionsToHtmlTags(text: string): string {
  return text.replace(
    /@\[([^\]]+)\]\(member:([^)\s]+)\)/g,
    (_, name: string, id: string) =>
      `<mention mid="${id}">${name}</mention>`,
  );
}

export function Markdown({ text, businessId, members, isStreaming }: Props) {
  // Tokens the model wrote become tags first; then any roster name it wrote
  // as plain text gets the same tag, so the pill does not depend on the model.
  const cleaned = linkRosterMentions(
    mentionsToHtmlTags(
      stripModelCopiedStorageLinks(
        stripIntelligenceLeaks(normalizeBrokenMemberMentions(text)),
      ),
    ),
    members,
    { isStreaming },
  );

  return (
    <div className="space-y-3 text-sm leading-[1.7] text-foreground">
      <Streamdown
        isAnimating={isStreaming}
        allowedTags={{ mention: ["mid"] }}
        literalTagContent={["mention"]}
        components={{
          mention: (props: Record<string, unknown>) => {
            const memberId = (props.mid ?? "") as string;
            const displayName = (
              typeof props.children === "string"
                ? props.children
                : String(props.children ?? "")
            ).trim();
            if (businessId && memberId) {
              return (
                <MemberMention
                  businessId={businessId}
                  memberId={memberId}
                  displayName={displayName}
                  roster={members ?? null}
                />
              );
            }
            return <span className="font-medium">{displayName}</span>;
          },
          a({ href, children }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-2 hover:text-primary/90"
              >
                {children}
              </a>
            );
          },
          h1: ({ children }) => (
            <p className="font-display text-[0.9375rem] font-normal">
              {children}
            </p>
          ),
          h2: ({ children }) => (
            <p className="text-sm font-semibold tracking-tight">{children}</p>
          ),
          h3: ({ children }) => (
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc space-y-1.5 pl-5">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal space-y-1.5 pl-5">{children}</ol>
          ),
          code: ({ children }) => (
            <code className="rounded bg-muted px-1 py-0.5 text-[0.85em] font-mono">
              {children}
            </code>
          ),
          p: ({ children }) => (
            <p className="whitespace-pre-wrap">{children}</p>
          ),
          // GFM tables the model writes itself (FORMAT rule in the prompt):
          // the same look as a typed table part, member pills in cells.
          table: ({ children }) => (
            <div className="w-fit max-w-full overflow-x-auto rounded-md border border-border bg-background">
              <table className="w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-muted/40 text-muted-foreground">{children}</thead>
          ),
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => (
            <tr className="border-b border-border last:border-b-0">{children}</tr>
          ),
          th: ({ children, style }) => (
            <th
              className="h-8 px-3 text-left align-middle font-medium whitespace-nowrap"
              style={style}
            >
              {children}
            </th>
          ),
          td: ({ children, style }) => (
            <td className="px-3 py-1.5 align-middle tabular-nums" style={style}>
              {children}
            </td>
          ),
        }}
      >
        {cleaned}
      </Streamdown>
    </div>
  );
}
